/**
 * Re-decides an open consultation's supervising physician (SP) after the patient's country
 * or province/state changed.
 *
 * The SP is decided when the chat opens, from where the patient is at that moment. Editing the
 * patient here did not re-decide it, so a patient moved from the United States to Canada kept
 * their US SP, and prescriptions went out under them (QA on staging, Oct 2026). The server
 * re-decides it with the same v1 rules a new chat gets (listener-reassignSupervisingPhysician
 * -> addAdminDoctor in yourdoctors-cloudfunctions listeners.js), which writes the new SP to
 * openconversations/{chatID}/adminDoctor, or clears it when the new region has none.
 * Staff only: the server checks the signed-in staff member's Firebase ID token.
 * Same helper as PHD's src/utils/reassignSupervisingPhysician.js; keep the two in step.
 */
import axios from "axios";
import {auth, database} from "../firebase";
import {firebaseNorthEastRoot} from "../config";

const norm = (value) => String(value || "").trim().toLowerCase();

/** True when a save moves the patient to another country or province/state. */
export const regionChanged = (before, after) =>
    norm(before?.country) !== norm(after?.country) || norm(before?.state) !== norm(after?.state);

/** The new SP's name, or null when the new region has none. Throws if it could not re-decide. */
export const reassignSupervisingPhysician = async (chatID) => {
    const idToken = await auth.currentUser.getIdToken();
    const response = await axios({
        method: "POST",
        url: `${firebaseNorthEastRoot}/listener-reassignSupervisingPhysician`,
        headers: {"Content-Type": "application/json", "Authorization": `Bearer ${idToken}`},
        data: {chatID},
    });
    const data = response?.data;
    if (!data || data.status !== "Success") {
        throw new Error(data?.error || "Could not re-assign the supervising physician");
    }
    // The prescription SP saved by an earlier submission belongs to the old region; left on
    // the chat it was returned at the next Canadian submit and checked instead of the new SP.
    await database.ref().update({[`openconversations/${chatID}/prescriptionDoctor`]: null});
    return data.adminDoctor || null;
};
